import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { KnowledgeSource } from '@vendasta/embeddings';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyColumnDef, GalaxyDataSource, GalaxyTableModule } from '@vendasta/galaxy/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map, take } from 'rxjs';

import { AddKnowledgeComponent } from '../../knowledge-editor/add-knowledge/add-knowledge.component';
import { BusinessProfileComponent } from '../../knowledge-editor/business-profile/business-profile.component';
import { CustomDataComponent } from '../../knowledge-editor/custom-data/custom-data.component';
import { FileUploadComponent } from '../../knowledge-editor/file-upload/file-upload.component';
import { WebsiteScrapeComponent } from '../../knowledge-editor/website-scrape/website-scrape.component';
import { DeleteCustomKnowledgeComponent } from '../../delete-custom-knowledge/delete-custom-knowledge.component';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import { FileTypeIconService } from '../../services/file-type-icon.service';
import { OWNED_SENTINEL, TableKnowledgeSource } from '../types';

/**
 * The Knowledge sources table view. Renders the `GalaxyDataSource` it's given inside a `glxy-table-container` and owns the source-level actions (add / edit / refresh / delete) plus the legacy `?editSource` / `?addSource` deep links. Mutations emit `changed` so the page can refetch.
 */
@Component({
  selector: 'ai-knowledge-knowledge-source-table',
  imports: [
    CommonModule,
    GalaxyBadgeModule,
    MatButtonModule,
    MatDialogModule,
    GalaxyEmptyStateModule,
    GalaxyTableModule,
    MatIconModule,
    MatMenuModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    TranslateModule,
    AiKnowledgeI18nModule,
  ],
  templateUrl: './knowledge-source-table.component.html',
  styleUrls: ['./knowledge-source-table.component.scss'],
})
export class KnowledgeSourceTableComponent {
  @Input({ required: true }) dataSource!: GalaxyDataSource<TableKnowledgeSource, unknown, unknown>;

  /** The full source rows — used only for the legacy deep-link lookup; rendering goes through `dataSource`. */
  @Input() set rows(value: TableKnowledgeSource[] | undefined) {
    this.allRows = value ?? [];
    this.maybeHandleDeepLink();
  }

  @Input() editingEnabled = true;

  /**
   * Collection-membership mode: show a per-row "Remove from collection" action. This is distinct from the
   * table's own edit/delete (which mutate the source itself) — removing from a collection only detaches the
   * membership, and the owning page handles it via {@link removeFromCollection}. Set true only on an owned
   * collection's detail page.
   */
  @Input() allowRemoveFromCollection = false;

  /** Raw sources by id; empty in @Input/storybook mode, which makes edit/refresh/delete no-ops. */
  @Input() set rawSourcesById(value: Map<string, KnowledgeSource>) {
    this.rawSources = value ?? new Map();
    this.maybeHandleDeepLink();
  }

  /** Emitted after an action mutates server state, so the page can refetch. */
  @Output() changed = new EventEmitter<void>();

  /** A row's "Remove from collection" was chosen; the page detaches it and refreshes (see {@link allowRemoveFromCollection}). */
  @Output() removeFromCollection = new EventEmitter<TableKnowledgeSource>();

  private readonly dialog = inject(MatDialog);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly fileTypeIconService = inject(FileTypeIconService);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);

  private rawSources = new Map<string, KnowledgeSource>();
  private allRows: TableKnowledgeSource[] = [];
  private deepLinkHandled = false;

  /**
   * Column metadata for the container (order, titles, sortability). The visible headers are translated
   * in the template via custom `*matHeaderCellDef` cells; these `title`s only back the sort menu / column
   * organizer (both off here) and the default header. Resolved reactively with `translate.stream` — its
   * first value comes from `get()`, which waits for the loader, so it never renders the raw key (unlike
   * `instant`), and it re-resolves on language change.
   *
   * Sorting is omitted: the table is server-paged and the list endpoint has no sort field, so a client
   * sort would only reorder the current page (misleading). Restore sortable once the API supports it.
   */
  private readonly columnDefs: ReadonlyArray<{ id: string; titleKey?: string }> = [
    { id: 'kind', titleKey: 'KNOWLEDGE_SHARING.SOURCE_TABLE.COL_TYPE' },
    { id: 'name', titleKey: 'KNOWLEDGE_SHARING.SOURCE_TABLE.COL_NAME' },
    { id: 'description', titleKey: 'KNOWLEDGE_SHARING.SOURCE_TABLE.COL_DESCRIPTION' },
    { id: 'lastModified', titleKey: 'KNOWLEDGE_SHARING.SOURCE_TABLE.COL_LAST_MODIFIED' },
    { id: 'actions' },
  ];

  readonly columns = toSignal(
    this.translate.stream(this.columnDefs.flatMap((c) => (c.titleKey ? [c.titleKey] : []))).pipe(
      map((dict: Record<string, string>): GalaxyColumnDef[] =>
        this.columnDefs.map((c) => ({ id: c.id, title: c.titleKey ? dict[c.titleKey] : '' })),
      ),
    ),
    { initialValue: this.columnDefs.map((c): GalaxyColumnDef => ({ id: c.id })) },
  );

  /** Stable row identity so re-emits from the datasource don't recreate rows (which would close an open row menu). */
  trackRow = (_: number, row: TableKnowledgeSource): string => row.id;

  isShared(row: TableKnowledgeSource): boolean {
    return row.managedBy !== '' && row.managedBy !== OWNED_SENTINEL;
  }

  /** Collection-membership remove is offered for non-shared rows when the table is in remove mode. */
  canRemoveFromCollection(row: TableKnowledgeSource): boolean {
    return this.allowRemoveFromCollection && !this.isShared(row);
  }

  /**
   * Whether the overflow menu would have any items — used to hide the trigger so it never opens empty
   * (an empty mat-menu flickers). Owned + editable rows always get at least the (possibly disabled) edit/delete pair;
   * in remove mode, non-shared rows get the "Remove from collection" item.
   */
  hasRowActions(row: TableKnowledgeSource): boolean {
    return (this.editingEnabled && !this.isShared(row)) || this.canRemoveFromCollection(row);
  }

  /** Drives the empty-state copy (no matches vs. nothing yet). */
  isFiltered(): boolean {
    return !!this.dataSource?.state.searchTerm;
  }

  iconFor(row: TableKnowledgeSource): string {
    // Use the file-type icon when the raw source is loaded; else fall back to kind.
    const raw = this.rawSources.get(row.id);
    if (raw) {
      return this.fileTypeIconService.getKnowledgeSourceIcon(raw);
    }
    switch (row.kind) {
      case 'url':
        return 'language';
      case 'pdf':
        return 'picture_as_pdf';
      case 'file':
        return 'description';
      case 'custom':
        return 'notes';
      default:
        return 'description';
    }
  }

  /** No edit/delete/refresh for shared rows, when editing is disabled, or in @Input mode (no raw source). */
  canEditSource(row: TableKnowledgeSource): boolean {
    if (this.isShared(row)) return false;
    if (!this.editingEnabled) return false;
    return this.rawSources.has(row.id);
  }

  /** Refresh affordance only makes sense for website-type sources. */
  canRefreshSource(row: TableKnowledgeSource): boolean {
    if (!this.canEditSource(row)) return false;
    const raw = this.rawSources.get(row.id);
    return Boolean(raw?.config?.websiteScrapeConfig);
  }

  /** Opens the editor for a source, picking the dialog by config type. */
  async openKnowledgeSourceEditor(row: TableKnowledgeSource): Promise<void> {
    const source = this.rawSources.get(row.id);
    if (!source) return; // storybook / no-raw mode — no-op
    if (!this.editingEnabled) return;
    const config = {
      width: '660px',
      data: {
        knowledgeSourceKey: { knowledgeSourceId: source.id, namespace: source.namespace },
        config: source.config,
      },
    };
    let editor;
    if (source.config?.websiteScrapeConfig) {
      editor = this.dialog.open(WebsiteScrapeComponent, config);
    } else if (source.config?.customDataConfig) {
      editor = this.dialog.open(CustomDataComponent, config);
    } else if (source.config?.fileConfig) {
      editor = this.dialog.open(FileUploadComponent, config);
    } else {
      editor = this.dialog.open(BusinessProfileComponent, config);
    }
    const navigatingAway = await firstValueFrom(editor.afterClosed());
    if (!navigatingAway) {
      this.changed.emit();
    }
  }

  async refreshKnowledgeSource(row: TableKnowledgeSource): Promise<void> {
    const source = this.rawSources.get(row.id);
    if (!source?.config?.websiteScrapeConfig) return;
    await this.knowledgeService.rescrapeWebsite(source);
    this.changed.emit();
  }

  async deleteKnowledgeSource(row: TableKnowledgeSource): Promise<void> {
    const source = this.rawSources.get(row.id);
    if (!source) return;
    const result = await firstValueFrom(
      this.dialog
        .open(DeleteCustomKnowledgeComponent, { width: '660px', data: { knowledgeSourceId: source.id } })
        .afterClosed(),
    );
    if (result) {
      this.changed.emit();
    }
  }

  /** Opens the source-type picker; signals a change when a source is created. */
  async openAddKnowledgeSourceDialog(): Promise<void> {
    if (!this.editingEnabled) return;
    const picker = this.dialog.open(AddKnowledgeComponent, { width: '660px' });

    // The picker closes the moment a type is chosen and hands off the second dialog's knowledgeCreated stream.
    // Each subscription is bounded by its own dialog's destroyRef (plus take(1)), so the inner stream lives
    // with the second dialog, not the picker.
    picker.componentInstance.componentSelected
      .pipe(takeUntilDestroyed(picker.componentInstance.destroyRef), take(1))
      .subscribe((event) => {
        event.knowledgeCreated
          .pipe(takeUntilDestroyed(event.destroyRef), take(1))
          .subscribe(() => this.changed.emit());
      });

    await firstValueFrom(picker.afterClosed());
  }

  /** Honor legacy ?editSource / ?addSource deep links once data is available. */
  private maybeHandleDeepLink(): void {
    if (this.deepLinkHandled) return;
    const params = this.activeRoute.snapshot.queryParamMap;
    const editSource = params.get('editSource');
    const addSource = params.get('addSource');
    if (!editSource && !addSource) return;

    if (editSource) {
      const colonIndex = editSource.indexOf(':');
      const id = colonIndex === -1 ? editSource : editSource.slice(0, colonIndex);
      const row = this.allRows.find((r) => r.id === id);
      if (!row || !this.rawSources.has(id)) return; // wait until both rows and raw sources are loaded
      this.deepLinkHandled = true;
      void this.openKnowledgeSourceEditor(row);
      return;
    }
    this.deepLinkHandled = true;
    void this.openAddKnowledgeSourceDialog();
  }
}
